import React from "react";
import TooltipWrapper from "../TooltipWrapper";
import RecordRow from "./RecordRow";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";
import { crmRecordKeyValue } from "../../../cf";
import Image from "../../Tags/Image";
import { useStoreIntegrations } from "../../../stores/useStoreIntegrations";
import dayjs from "dayjs";

export default function CrmRecord({
  data = {},
  diffData = {},
  record = [],
  apiName = "",
  crmObjectsConfig,
  type = "",
  convertedCRMObjectApiName = "",
}) {
  const { activeCRMIntegration } = useStoreIntegrations();
  const captureEvent = useCaptureEvent();
  const recordId = data?.Id || data?.crm_record_id || data?.id;
  const diffArr = (arr, diff) => {
    if (arr && arr.length > 0 && diff) {
      try {
        const arrayKeys = arr.map((item) => item.key);

        const filteredDiff = Object.keys(diff)
          .filter((diffKey) => !arrayKeys.includes(diffKey))
          .reduce((acc, diffKey) => {
            acc[diffKey] = diff[diffKey];
            return acc;
          }, {});

        return filteredDiff;
      } catch (err) {
        return {};
      }
    }
    return {};
  };

  const DataRow = ({ label = "", existingValue = "", newValue = "", diff = false }) => (
    <>
      {diff ? (
        <>
          {" "}
          <span className="text-sm block w-full">{label}:</span>
          <span className="text-gray-900 text-sm font-semibold break-words block">
            <div className="rounded overflow-hidden">
              <TooltipWrapper content={"Current data in CRM record"} className="w-full">
                <span className="bg-error-50 py-1 px-2 flex items-center">
                  <span className="inline-flex border border-error-200 bg-error-50 text-error-700 text-[9px] py-0.5 px-1 rounded-lg leading-none mr-1">
                    Now
                  </span>
                  <span className="text-gray-600 font-normal flex-1 whitespace-break-spaces text-left pr-word-break">
                    {existingValue
                      ? typeof existingValue === "string" &&
                        dayjs(existingValue).isValid() &&
                        existingValue.includes("T")
                        ? dayjs(existingValue).format("DD MMM YYYY, hh:mm:ss A")
                        : existingValue
                      : "-"}
                  </span>
                </span>
              </TooltipWrapper>
              <TooltipWrapper content={"Latest refreshed data"} className="w-full">
                <span className="bg-success-50 py-1 px-2 flex items-center">
                  <span className="inline-flex border border-success-200 bg-success-50 text-success-700 text-[9px] py-0.5 px-1 rounded-lg leading-none mr-1">
                    New
                  </span>

                  <span className="text-gray-600 font-normal flex-1 whitespace-break-spaces text-left pr-word-break">
                    {newValue
                      ? typeof newValue === "string" && dayjs(newValue).isValid() && newValue.includes("T")
                        ? dayjs(newValue).format("DD MMM YYYY, hh:mm:ss A")
                        : newValue
                      : "-"}
                  </span>
                </span>
              </TooltipWrapper>
            </div>
          </span>{" "}
        </>
      ) : (
        <>
          <span className="text-sm block w-full">{label}:</span>
          <span className="text-gray-900 text-sm font-semibold break-words block">
            <span className="inline-block" style={{ wordBreak: "break-word" }}>
              {existingValue}
            </span>
          </span>
        </>
      )}
    </>
  );

  return (
    <div>
      <span className="block mb-4">
        <span className="flex w-full items-center justify-between space-x-2 " target="_blank">
          <span className="inline-flex items-center space-x-2">
            {" "}
            {data?.match_confirmed ? (
              <Image
                src={`/images/crm/${activeCRMIntegration?.integration?.key}-blue.svg`}
                className="h-4 no-invert"
              />
            ) : (
              <Image
                src={`/images/crm/${activeCRMIntegration?.integration?.key}-gray.svg`}
                className="h-4 no-invert"
              />
            )}
            <span className="text-gray-900 text-sm font-semibold">
              {data?.Name ? (
                data?.Name
              ) : (
                <>
                  {data?.data?.name?.value
                    ? data?.data?.name?.value
                    : `${data?.data?.first_name?.value} ${data?.data?.last_name?.value}`}
                </>
              )}
            </span>
          </span>
          <a
            href={data?.record_url}
            target="_blank"
            onClick={() => {
              captureEvent("ECRM_RedirectToSF", {
                type: type,
              });
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M17 10.3333L17 7M17 7H13.6667M17 7L12.5556 11.4444M10.8889 8.11111H9.66667C8.73325 8.11111 8.26654 8.11111 7.91002 8.29277C7.59641 8.45256 7.34144 8.70752 7.18166 9.02113C7 9.37765 7 9.84436 7 10.7778V14.3333C7 15.2668 7 15.7335 7.18166 16.09C7.34144 16.4036 7.59641 16.6586 7.91002 16.8183C8.26654 17 8.73325 17 9.66667 17H13.2222C14.1556 17 14.6224 17 14.9789 16.8183C15.2925 16.6586 15.5474 16.4036 15.7072 16.09C15.8889 15.7335 15.8889 15.2668 15.8889 14.3333V13.1111"
                stroke="var(--gray-600)"
                stroke-width="1.11111"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </a>
        </span>
        {(apiName || recordId) && (
          <span className="flex flex-wrap items-center gap-2 mt-4">
            {apiName && (
              <span className="border border-gray-blue-200 rounded-xl text-sm bg-transparent text-gray-blue-700 px-2 inline-block">
                {convertedCRMObjectApiName ? convertedCRMObjectApiName : apiName}
              </span>
            )}
            {recordId && (
              <span className="text-xs text-gray-600 min-w-0">
                ID:{" "}
                {data?.record_url ? (
                  <a
                    href={data?.record_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-brand-700 hover:underline break-all"
                    onClick={() => {
                      captureEvent("ECRM_RedirectToSF", {
                        type: type,
                        source: "record_id",
                      });
                    }}
                  >
                    {recordId}
                  </a>
                ) : (
                  <span className="text-gray-900 break-all">{recordId}</span>
                )}
              </span>
            )}
          </span>
        )}
      </span>
      <div className="space-y-2">
        {data &&
          crmObjectsConfig &&
          crmObjectsConfig.length > 0 &&
          crmRecordKeyValue(type, data?.apiName, crmObjectsConfig) &&
          crmRecordKeyValue(type, data?.apiName, crmObjectsConfig).length > 0 &&
          crmRecordKeyValue(type, data?.apiName, crmObjectsConfig).map((el) => {
            if (el.system_field_name !== "location" && !diffData[el.label]) {
              return (
                <>
                  {el.system_field_name !== "crm_owner_name" && (
                    <RecordRow
                      label={el?.label}
                      value={
                        el?.crm_field_api_name
                          ? el.system_field_name === "company" && data?.crm_resolved_company_name
                            ? data?.crm_resolved_company_name
                            : data?.data?.[el.system_field_name]?.value
                          : ""
                      }
                    />
                  )}
                  {el.system_field_name === "crm_owner_name" && (
                    <RecordRow label={el?.label} value={data?.crm_owner_name} />
                  )}
                </>
              );
            }
            if (el.system_field_name === "location") {
              return (
                <RecordRow
                  label={el?.label}
                  value={null}
                  location={true}
                  locationData={
                    type === "company"
                      ? {
                          city: data?.data?.headquarters_city?.value,
                          state: data?.data?.headquarters_state?.value,
                          country: data?.data?.headquarters_country?.value,
                        }
                      : {
                          city: data?.data?.city?.value,
                          state: data?.data?.state?.value,
                          country: data?.data?.country?.value,
                        }
                  }
                />
              );
            }
          })}
        {record &&
          record.length > 0 &&
          record.map((item) => (
            <>
              {diffData[item?.key] && diffData[item?.key]?.new_value !== item?.value ? (
                <div className="space-y-1 mb-3">
                  <DataRow
                    label={item?.label}
                    diff={true}
                    existingValue={diffData[item?.key]?.existing_value}
                    newValue={diffData[item?.key]?.new_value}
                  />
                </div>
              ) : (
                <></>
              )}
            </>
          ))}
        {record &&
          record.length > 0 &&
          diffArr(record, diffData) &&
          Object.keys(diffArr(record, diffData)).length > 0 &&
          Object.entries(diffArr(record, diffData)).map(([key, value]) => (
            <div className="space-y-1 mb-3">
              <DataRow
                label={value?.label || key}
                diff={true}
                existingValue={value?.existing_value}
                newValue={value?.new_value}
              />
            </div>
          ))}
      </div>
    </div>
  );
}
